/**
 * Czech-localised formatters for money, percentages, basis points, dates.
 *
 * Never stringify numbers manually — use these helpers so UI stays consistent.
 */

const CZK = new Intl.NumberFormat("cs-CZ", {
  maximumFractionDigits: 0,
  useGrouping: true,
});

const CZK_COMPACT = new Intl.NumberFormat("cs-CZ", {
  notation: "compact",
  maximumFractionDigits: 1,
});

const PERCENT = new Intl.NumberFormat("cs-CZ", {
  style: "percent",
  minimumFractionDigits: 1,
  maximumFractionDigits: 2,
});

const PERCENT_INT = new Intl.NumberFormat("cs-CZ", {
  style: "percent",
  maximumFractionDigits: 0,
});

/** `2 340 000` — no currency symbol. Add "Kč" separately for styling. */
export function fmtInt(value: number): string {
  return CZK.format(value);
}

/** `+2 340 000` / `−184 500` — signed, Czech minus. */
export function fmtSigned(value: number): string {
  if (value === 0) return "0";
  const sign = value > 0 ? "+" : "−";
  return `${sign}${CZK.format(Math.abs(value))}`;
}

/** `2,3 M` / `870 K` — compact for small cards. */
export function fmtCompact(value: number): string {
  return CZK_COMPACT.format(value);
}

/** Basis points → percent, 1 decimal. `475` → `4,75 %`. */
export function fmtBpsPercent(bps: number): string {
  return PERCENT.format(bps / 10_000);
}

/** `42 %` — integer percent from a fraction 0–1. */
export function fmtPercentInt(fraction: number): string {
  return PERCENT_INT.format(fraction);
}

/** `+4.2 %` — signed percent from a fraction. */
export function fmtSignedPercent(fraction: number): string {
  if (fraction === 0) return "0 %";
  const sign = fraction > 0 ? "+" : "−";
  return `${sign}${PERCENT.format(Math.abs(fraction))}`;
}

/** Game month number → Czech calendar label (`36` → `leden 2029`). */
const CZECH_MONTHS = [
  "leden",
  "únor",
  "březen",
  "duben",
  "květen",
  "červen",
  "červenec",
  "srpen",
  "září",
  "říjen",
  "listopad",
  "prosinec",
];

export function fmtGameMonth(month: number, startYear = 2025): string {
  const yearOffset = Math.floor(month / 12);
  const monthIdx = month % 12;
  return `${CZECH_MONTHS[monthIdx]} ${startYear + yearOffset}`;
}
